import _ from "lodash";
import client from "lib/apolloClient/client";
import { MONITORING_FRAGMENTS } from "./WorksheetMonitoringQueries";

// * Get single student data from worksheet monitoring query
export const getStudentAssignmentItemFromCache = id => {
  if (_.isNil(id)) return null;
  try {
    return client.readFragment({
      id: `StudentAssignment:${id}`,
      fragment: MONITORING_FRAGMENTS.assignedStudent,
      fragmentName: "assignedStudentItem",
      variables: {
        showOnlySubmitted: false,
        entityEventsFilter: {
          orderBy: "EVENT_TIME",
          orderByDir: "ASC",
        },
      },
    });
  } catch (e) {
    return null;
  }
};

// * Write single student data to Apollo cache
export const writeStudentAssignmentItemInCache = (id, data) => {
  if (_.isNil(id) || _.isNil(data)) return null;
  try {
    client.writeFragment({
      id: `StudentAssignment:${id}`,
      fragment: MONITORING_FRAGMENTS.assignedStudent,
      fragmentName: "assignedStudentItem",
      variables: {
        showOnlySubmitted: false,
        entityEventsFilter: {
          orderBy: "EVENT_TIME",
          orderByDir: "ASC",
        },
      },
      data,
    });
    return getStudentAssignmentItemFromCache(id);
  } catch (e) {
    return null;
  }
};

/**
 * @description Per-student lock map to serialize cache writes.
 * Prevents two simultaneous events for the same student from
 * reading the same base state and both writing — losing one event.
 */
const cacheWriteLocks = new Map();

/**
 *
 * @param {*} id
 * @param {*} updater
 * @description Update student assignment events in Apollo cache.
 * Writes are serialized per student via a promise-based lock.
 * Different students can update in parallel.
 */
export const updateStudentEventsInCache = (id, updater) => {
  if (_.isNil(id)) return;
  const prevLock = cacheWriteLocks.get(id) || Promise.resolve();
  const currentLock = prevLock
    .then(() => {
      const cacheData = getStudentAssignmentItemFromCache(id);
      const currentEvents = _.get(cacheData, "events", []) || [];
      const updatedEvents = updater(currentEvents);
      const data = { ...cacheData, events: updatedEvents };
      writeStudentAssignmentItemInCache(id, data);
    })
    .catch(() => {
      // Silently recover — cache read/write failures are already
      // handled by getStudentAssignmentItemFromCache returning null
    })
    .finally(() => {
      // Clean up lock entry when no more operations are queued
      if (cacheWriteLocks.get(id) === currentLock) {
        cacheWriteLocks.delete(id);
      }
    });
  cacheWriteLocks.set(id, currentLock);
};
